import { useEffect, useState } from 'react';
import api from '../services/api';
import eventService from '../services/eventService';
import organizerService from '../services/organizerService';
import releaseNotesService from '../services/releaseNotesService';
import inquiryService from '../services/inquiryService';
import organizerAccessService from '../services/organizerAccessService';
import bookingService from '../services/bookingService';
import outreachService from '../services/outreachService';

/**
 * Outstanding-moderation counts, keyed by sidebar route so the Sidebar can render a
 * badge next to each item. Fetched once for moderators/admins and refreshed on demand:
 * a moderation page calls {@link notifyPendingCountsChanged} after processing an item,
 * which re-runs the fetch — no polling, no refetch on every navigation.
 *
 * Die Zahlen liegen in einem Modul-Store: Sidebar und Reiter-Seiten lesen denselben Stand,
 * statt je Einhängung zehn Endpunkte abzufragen.
 */
export type PendingCounts = Record<string, number>;

/** Pfade der Reiter-Seiten; die Sidebar zeigt am Abschnitt die Summe der Reiter. */
export const PENDING_PATHS = {
  eventEdits: '/moderation/vorschlaege/events',
  organizerEdits: '/moderation/vorschlaege/veranstalter',
  imageProposals: '/moderation/vorschlaege/bilder',
  eventDuplicates: '/moderation/duplikate/events',
  organizerDuplicates: '/moderation/duplikate/veranstalter',
} as const;

let current: PendingCounts = {};
const subscribers = new Set<(c: PendingCounts) => void>();
let enabledSubscribers = 0;

const settle = <T,>(p: Promise<T>): Promise<T | null> => p.then(v => v).catch(() => null);

const load = (): void => {
  Promise.all([
    settle(eventService.getPendingEdits(0, 1)),
    settle(api.get('/event-image-proposals/pending-count').then(r => r.data as { count: number })),
    settle(eventService.getDuplicates()),
    settle(organizerService.getDuplicates()),
    settle(organizerService.getPendingEdits(0, 1)),
    settle(releaseNotesService.unreadCount()),
    settle(inquiryService.getUnhandledInquiryCount()),
    settle(organizerAccessService.pendingCount()),
    settle(bookingService.openCount()),
    settle(outreachService.unreadCount()),
  ]).then(([eventEdits, imageCount, eventDups, orgDups, orgEdits, unreadReleaseNotes, unhandledInquiries, organizerAccess, openBookings, outreachReplies]) => {
    if (enabledSubscribers === 0) return;
    current = {
      [PENDING_PATHS.eventEdits]: eventEdits?.totalElements ?? 0,
      [PENDING_PATHS.imageProposals]: imageCount?.count ?? 0,
      [PENDING_PATHS.organizerEdits]: orgEdits?.totalElements ?? 0,
      [PENDING_PATHS.eventDuplicates]: eventDups?.length ?? 0,
      [PENDING_PATHS.organizerDuplicates]: orgDups?.length ?? 0,
      // Kein Moderationsrueckstand, aber dieselbe Mechanik: zeigt ungelesene Neuerungen an.
      '/admin/neuerungen': unreadReleaseNotes ?? 0,
      // Anfragen, die noch niemand auf "Erledigt" gesetzt hat.
      '/admin/anfragen': unhandledInquiries?.count ?? 0,
      // Antraege "Ich bin der Veranstalter", die noch niemand entschieden hat.
      '/organizers/zugaenge': organizerAccess?.count ?? 0,
      // Buchungsanfragen, die noch niemand bearbeitet hat.
      '/admin/buchungen': openBookings?.count ?? 0,
      // Antworten aus dem gemeinsamen Postfach, auf die noch niemand reagiert hat.
      '/admin/outreach': outreachReplies ?? 0,
    };
    subscribers.forEach(s => s(current));
  });
};

export const notifyPendingCountsChanged = (): void => {
  if (enabledSubscribers > 0) load();
};

export const usePendingCounts = (enabled: boolean): PendingCounts => {
  const [counts, setCounts] = useState<PendingCounts>(enabled ? current : {});

  useEffect(() => {
    if (!enabled) { setCounts({}); return; }
    subscribers.add(setCounts);
    enabledSubscribers += 1;
    // Der erste Abonnent laedt, alle weiteren lesen den vorhandenen Stand.
    if (enabledSubscribers === 1) load(); else setCounts(current);
    return () => {
      subscribers.delete(setCounts);
      enabledSubscribers -= 1;
      if (enabledSubscribers === 0) current = {};
    };
  }, [enabled]);

  return counts;
};

export default usePendingCounts;
